import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { MessageService } from '../message/message.service';
import { IReport } from '../report/report.model';
import { ReportService } from '../report/report.service';
import { ITelerikReport } from './telerik-report.model';

@Injectable()
export class TelerikService {
  reportUrl: any;
  reportBlob: Blob;

  constructor(
    private apiService: ApiService,
    private messageService: MessageService,
    private reportService: ReportService
  ) {}

  public generateReport(report: ITelerikReport): Promise<ITelerikReport> {
    return new Promise((resolve, reject) => {
      this.registerClient()
        .then(response => {
          report.clientId = response['clientId'];
          this.createReportInstance(report.clientId, report.name, report.params)
            .then(i => {
              report.instanceId = i['instanceId'];
              this.createDocument(
                report.clientId,
                report.instanceId,
                report.format
              )
                .then(d => {
                  report.documentId = d['documentId'];
                  resolve(report);
                })
                .catch(() => {
                  this.destroyReportInstance(
                    report.clientId,
                    report.instanceId
                  ).then(() => {
                    reject(report);
                  });
                });
            })
            .catch(() => {
              this.unregisterClient(report.clientId).then(() => {
                reject(report);
              });
            });
        })
        .catch(() => {
          reject(report);
        });
    });
  }

  public openReport(telerikReport: ITelerikReport) {
    if (telerikReport.documentId) {
      this.getDocumentInfo(
        telerikReport.clientId,
        telerikReport.instanceId,
        telerikReport.documentId
      )
        .then(responseInfo => {
          const documentReady = responseInfo['documentReady'];
          if (!documentReady) {
            setTimeout(() => {
              this.openReport(telerikReport);
            }, 500);
          } else {
            this.setBlobAndObjectUrl(telerikReport).then(arrayBuffer => {
              const report = <IReport>{
                arrayBuffer: arrayBuffer,
                title: telerikReport.reportTitle,
                name: 'Your report has been created',
                format: telerikReport.format
              };
              this.reportService.validateAndOpenReport(report);
            });
          }
        })
        .catch(() => {
          this.messageService.showMessage(
            MessageTypeEnum.Danger,
            'An error occured trying to open your report, plese try again'
          );
        });
    } else {
      this.messageService.showMessage(
        MessageTypeEnum.Danger,
        'An error occured trying to open your report, plese try again'
      );
    }
  }

  private setBlobAndObjectUrl(report: ITelerikReport): Promise<any> {
    return this.getDocument(
      report.clientId,
      report.instanceId,
      report.documentId
    ).catch(() => {
      this.reportUrl = undefined;
      this.reportBlob = undefined;
    });
  }

  private registerClient(): Promise<any> {
    return this.apiService.post(`api/reports/clients`, null);
  }

  private unregisterClient(clientId: string): Promise<any> {
    return this.apiService.delete(`api/reports/clients/${clientId}`, null);
  }

  private createReportInstance(
    clientId: string,
    reportName: string,
    reportParams?: string
  ): Promise<any> {
    const reportSource = {
      report: reportName,
      parameterValues: reportParams
    };
    return this.apiService.post(
      `api/reports/clients/${clientId}/instances`,
      reportSource
    );
  }

  private destroyReportInstance(
    clientId: string,
    instanceId: string
  ): Promise<any> {
    return this.apiService.delete(
      `api/reports/clients/${clientId}/instances/${instanceId}`,
      null
    );
  }

  private createDocument(
    clientId: string,
    instanceId: string,
    format: string
  ): Promise<any> {
    const formatKey = {
      format: format
    };
    return this.apiService.post(
      `api/reports/clients/${clientId}/instances/${instanceId}/documents`,
      formatKey
    );
  }

  private getDocument(
    clientId: string,
    instanceId: string,
    documentId: string
  ): Promise<any> {
    return this.apiService.get(
      `api/reports/clients/${clientId}/instances/${instanceId}/documents/${documentId}`,
      null,
      { responseType: 'arraybuffer' }
    );
  }

  private getDocumentInfo(
    clientId: string,
    instanceId: string,
    documentId: string
  ): Promise<any> {
    return this.apiService.get(
      `api/reports/clients/${clientId}/instances/${instanceId}/documents/${documentId}/info`,
      null
    );
  }
}
